import { Link } from "react-router-dom";
import { useState } from "react";
import { User, GraduationCap, Users, Building2, ArrowRight, Check } from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub } from "@/components/web/atoms";
import { Reveal, trackHomeEvent } from "@/components/web/home/primitives";

/* Section 11 — audience tabs (keyboard-accessible). */
const TABS = [
  {
    key: "individual", label: "Individual", icon: User, event: "individual-tab", to: "/individuals",
    headline: "Your personal research and knowledge workspace.",
    benefits: ["AI Research", "Multiple models", "Projects", "Tasks", "Documents", "Personal memory"],
    cta: "Explore Individuals",
  },
  {
    key: "student", label: "Student", icon: GraduationCap, event: "student-tab", to: "/students",
    headline: "A smarter workspace for group projects.",
    benefits: ["Group collaboration", "Separate AI research", "Files", "Tasks", "Deadlines", "Shared findings"],
    cta: "Explore Students",
  },
  {
    key: "team", label: "Team", icon: Users, event: "team-tab", to: "/teams",
    headline: "Bring people, AI and work together.",
    benefits: ["Chat", "AI research", "Meetings", "Projects", "Tasks", "Knowledge", "Automations"],
    cta: "Explore Teams",
  },
  {
    key: "business", label: "Business", icon: Building2, event: "business-tab", to: "/business",
    headline: "Turn company activity into organizational intelligence.",
    benefits: ["Role Intelligence", "Institutional memory", "Connectors", "Knowledge continuity", "Governance", "Successor transfer"],
    cta: "Explore Business",
  },
];

export default function AudienceTabs() {
  const [active, setActive] = useState("individual");
  const tab = TABS.find((t) => t.key === active) || TABS[0];
  return (
    <section
      id="audiences"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] scroll-mt-20"
      data-testid="home-audience-tabs"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">Who it's for</Eyebrow>
          <SectionTitle>TeamNest grows with you.</SectionTitle>
          <SectionSub className="mt-5">
            The same connected workspace, scaled to how you work — solo, in a study group, on a team, or across a business.
          </SectionSub>
        </Reveal>

        <div className="mt-10">
          <div role="tablist" aria-label="Audiences" className="inline-flex flex-wrap items-center gap-1.5 rounded-full border border-[var(--w-hairline)] bg-[var(--w-bg2)] p-1.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={active === t.key}
                data-testid={t.event}
                onClick={() => { setActive(t.key); trackHomeEvent(t.event, { source: "audience_tabs" }); }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13.5px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--w-brand)] ${
                  active === t.key ? "bg-[var(--w-brand)] text-black" : "text-[var(--w-text-dim)] hover:text-[var(--w-text)]"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          <Reveal key={active} className="mt-8">
            <div className="rounded-[20px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-6 sm:p-8" role="tabpanel">
              <h3 className="text-[22px] sm:text-[26px] font-bold tracking-[-0.02em] text-[var(--w-text)] max-w-[28ch]">
                {tab.headline}
              </h3>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tab.benefits.map((b) => (
                  <div key={b} className="flex items-center gap-2 text-[14px] text-[var(--w-text-dim)]">
                    <Check className="w-4 h-4 text-[var(--w-brand)] shrink-0" /> {b}
                  </div>
                ))}
              </div>
              <Link
                to={tab.to}
                data-testid={`${tab.key}-cta`}
                onClick={() => trackHomeEvent(tab.event, { source: "audience_cta" })}
                className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--w-brand)] hover:brightness-110"
              >
                {tab.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
